import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import moment from 'moment';
import { GTFonts, PoppinsFonts, useTheme } from '../../theme';
import { s, fs, v } from '../../utils/libs';
import { Button as PaperButton } from 'react-native-paper';
import DatePickerField from './DatePickerField';

type Mode = 'daily' | 'monthly' | 'yearly' | 'monthyear' | 'yearonly';

type Props = {
  title: string;
  mode?: Mode;
  initialStartISO?: string;
  initialEndISO?: string;
  minimumStartDate?: Date;
  onApply: (fromISO: string, toISO: string) => void | Promise<any>;
};

const Filter: React.FC<Props> = ({
  title,
  mode = 'daily',
  initialStartISO,
  initialEndISO,
  minimumStartDate,
  onApply,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [fromDate, setFromDate] = useState<Date>(() =>
    initialStartISO
      ? new Date(initialStartISO)
      : moment().startOf('month').toDate(),
  );
  const [toDate, setToDate] = useState<Date>(() =>
    initialEndISO ? new Date(initialEndISO) : moment().endOf('day').toDate(),
  );
  const [monthYearDate, setMonthYearDate] = useState<Date>(() =>
    initialStartISO ? new Date(initialStartISO) : moment().toDate(),
  );
  const [submitting, setSubmitting] = useState(false);

  const apply = async () => {
    if (mode === 'monthyear') {
      const start = moment(monthYearDate).startOf('month');
      const end = moment(monthYearDate).endOf('month');
      const maybePromise = onApply(start.toISOString(), end.toISOString());
      if (maybePromise && typeof (maybePromise as any).then === 'function') {
        try {
          setSubmitting(true);
          await (maybePromise as Promise<any>);
        } finally {
          setSubmitting(false);
        }
      }
      return;
    }
    if (mode === 'yearonly') {
      const start = moment(monthYearDate).startOf('year');
      const end = moment(monthYearDate).endOf('year');
      const maybePromise = onApply(start.toISOString(), end.toISOString());
      if (maybePromise && typeof (maybePromise as any).then === 'function') {
        try {
          setSubmitting(true);
          await (maybePromise as Promise<any>);
        } finally {
          setSubmitting(false);
        }
      }
      return;
    }
    let start = moment(fromDate);
    let end = moment(toDate);
    if (mode === 'daily') {
      start = start.startOf('day');
      end = end.endOf('day');
    } else if (mode === 'monthly') {
      start = start.startOf('month');
      end = end.endOf('month');
    } else if (mode === 'yearly') {
      start = start.startOf('year');
      end = end.endOf('year');
    }
    const maybePromise = onApply(start.toISOString(), end.toISOString());
    if (maybePromise && typeof (maybePromise as any).then === 'function') {
      try {
        setSubmitting(true);
        await (maybePromise as Promise<any>);
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Keep the internal state in sync with prop changes so the UI matches caller's dates
  useEffect(() => {
    if (initialStartISO) setFromDate(new Date(initialStartISO));
  }, [initialStartISO]);
  useEffect(() => {
    if (initialEndISO) setToDate(new Date(initialEndISO));
  }, [initialEndISO]);
  useEffect(() => {
    if (initialStartISO) setMonthYearDate(new Date(initialStartISO));
  }, [initialStartISO]);

  const maxDate = useMemo(() => new Date(), []);
  const handleFromDateChange = useCallback((date: Date | null) => {
    if (date) setFromDate(date);
  }, []);
  const handleToDateChange = useCallback((date: Date | null) => {
    if (date) setToDate(date);
  }, []);
  const handleMonthYearDateChange = useCallback((date: Date | null) => {
    if (date) setMonthYearDate(date);
  }, []);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{title}</Text>
      {mode === 'monthyear' || mode === 'yearonly' ? (
        <View style={styles.row}>
          <View style={styles.field}>
            <DatePickerField
              value={monthYearDate}
              onChange={date => {
                if (date) setMonthYearDate(date);
              }}
              label={mode === 'yearonly' ? 'Year only' : 'Month & Year'}
              displayFormat={mode === 'yearonly' ? 'yearOnly' : 'monthYear'}
              variant="compact"
              inputStyle={styles.dateInput}
              maximumDate={maxDate}
            />
          </View>
          <View style={styles.applyWrap}>
            <PaperButton
              mode="contained"
              onPress={apply}
              loading={submitting}
              disabled={submitting}
              buttonColor={theme.colors.primary}
              compact
              style={styles.applyButton}
              contentStyle={styles.applyButtonContent}
              labelStyle={{
                fontFamily: PoppinsFonts.regular,
                fontSize: fs(12),
                color: '#fff',
              }}>
              Filter
            </PaperButton>
          </View>
        </View>
      ) : (
        <View style={styles.row}>
          <View style={styles.field}>
            <DatePickerField
              value={fromDate}
              onChange={date => {
                if (date) setFromDate(date);
              }}
              label="From"
              displayFormat="iso"
              variant="compact"
              inputStyle={styles.dateInput}
              minimumDate={minimumStartDate}
              maximumDate={maxDate}
            />
          </View>
          <View style={styles.field}>
            <DatePickerField
              value={toDate}
              onChange={date => {
                if (date) setToDate(date);
              }}
              label="To"
              displayFormat="iso"
              variant="compact"
              inputStyle={styles.dateInput}
              maximumDate={maxDate}
            />
          </View>
          <View style={styles.applyWrap}>
            <PaperButton
              mode="contained"
              onPress={apply}
              loading={submitting}
              disabled={submitting}
              buttonColor={theme.colors.primary}
              compact
              style={styles.applyButton}
              contentStyle={styles.applyButtonContent}
              labelStyle={{
                fontFamily: PoppinsFonts.regular,
                fontSize: fs(12),
                color: '#fff',
              }}>
              Filter
            </PaperButton>
          </View>
        </View>
      )}
    </View>
  );
};

export default Filter;

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.surface,
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: theme.borderRadius.md,
      padding: s(10),
      marginBottom: v(10),
    },
    title: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      fontSize: fs(16),
      lineHeight: fs(20),
      marginBottom: v(8),
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: s(8) as unknown as number,
    },
    field: {
      flex: 1,
    },
    dateInput: {
      height: v(44),
      minHeight: v(44),
      paddingVertical: 0,
    },
    applyWrap: {
      width: s(60),
      alignSelf: 'flex-end',
      marginBottom: v(6),
    },
    applyButton: {
      borderRadius: theme.borderRadius.md,
      width: s(60),
    },
    applyButtonContent: {
      height: v(44),
    },
  });
